import type { InputFrame } from "../input";
import { groundKind, railHit, surfaceAt } from "../level";
import type { Skater } from "../skater";
import { enterAir, noteAirTrick } from "./air";
import { isRide } from "./rides";
import type { FootState, LedgeState } from "./state";
import {
  CAVEMAN_POINTS,
  FOOT_ACCEL,
  FOOT_BACK,
  FOOT_BOARD_ON,
  FOOT_CLIMB,
  FOOT_DROP,
  FOOT_HANG,
  FOOT_JUMP,
  FOOT_LEDGE_IN,
  FOOT_MAX_SLOPE,
  FOOT_REACH,
  FOOT_RUN,
  FOOT_STEP,
  FOOT_STEP_RUN,
  FOOT_STEP_WALK,
  FOOT_TURN,
  G,
  LAND_LINK_WINDOW,
  RUN_OUT,
  STEP_OFF_CHORD,
  STEP_OFF_SLOPE,
  WASH_SHOVE,
  WASH_SHOVE_SLOPE,
} from "./tuning";

/** Rounding room on `FOOT_STEP`, so a stair set at the lowest rise (0.3 m, #163) walks up every step, not most. */
const STEP_EPS = 1e-6;

// Off the board (#19): L2 + R2 together steps off (THPS Underground's two
// triggers) and the same two get back on. On foot the stick walks (up runs,
// part way walks, down backs up) and turns, like steering the board, and X
// jumps. You step up kerbs but not onto boxes (jump for those), can't climb
// steep ramps, drop off ledges, and walls, rails, the fence and the sea stop
// you. The board's carried; everything about being on foot is in `FootState`.
// The triggers in a jump are a Caveman (THPS 3): the board drops under your
// feet in the air and you land on it, a trick that starts a combo. Stepping
// off mid-combo (just landed, or in a manual) is a run out (THPS
// Underground): the combo waits `RUN_OUT` seconds on foot for a Caveman to
// carry it on, or getting back on (then a manual keeps it), and banks after.
// Jumping at a ledge a bit too high to hop onto catches it (THPS
// Underground): you hang there until up or X climbs you over it, or down
// lets go. Spraying the power washer standing on a slope shoves you back.

/** Nollie and stance as they were before the chord's first trigger, to put back if the chord is acted on. */
export interface Chord {
  nollie: boolean;
  switchStance: boolean;
}

/**
 * Tracks the two triggers for the step-off chord. Called every step before
 * the mode's own step. The first trigger press still does its own thing
 * (nollie, switch stance) at once; returns the chord when the other follows
 * within `STEP_OFF_CHORD` (or both go down together), so whoever acts on it
 * can undo that first press with `undoChord`.
 */
export function readChord(sk: Skater, input: InputFrame, dt: number): Chord | null {
  const c = sk.chord;
  const nollie = input.pressed.nollie;
  const revert = input.pressed.revert;
  if (c) c.t += dt;
  if (nollie && revert) {
    sk.chord = null;
    return { nollie: sk.nollie, switchStance: sk.switchStance };
  }
  if (!nollie && !revert) {
    if (c && (c.t > STEP_OFF_CHORD || (!input.held.nollie && !input.held.revert))) sk.chord = null;
    return null;
  }
  const other = nollie ? "revert" : "nollie";
  if (c && c.first === other && c.t <= STEP_OFF_CHORD && input.held[other]) {
    sk.chord = null;
    return { nollie: c.nollie, switchStance: c.switchStance };
  }
  sk.chord = { first: nollie ? "nollie" : "revert", t: 0, nollie: sk.nollie, switchStance: sk.switchStance };
  return null;
}

/** The chord's first trigger was the start of the chord, not a move: put nollie and stance back. */
export function undoChord(sk: Skater, c: Chord): void {
  sk.nollie = c.nollie;
  sk.switchStance = c.switchStance;
}

/**
 * Whether the skater can step off here and now: rolling on flat-ish ground,
 * outside a combo or with one still live (just landed, or in a manual),
 * and never on a hill.
 */
export function canStepOff(sk: Skater): boolean {
  // On the hill (#195) the ride's picked at the top and kept to the end of the run.
  if (sk.mode !== "ground" || sk.skitch || sk.manual?.pop || sk.hill) return false;
  if (sk.combo.active && !sk.manual && sk.linkWindow <= 0) return false;
  const s = surfaceAt(sk.level, sk.x, sk.z);
  return !s.quarter && Math.hypot(s.gx, s.gz) <= STEP_OFF_SLOPE;
}

/** Off the board, facing the way it was rolling (fakie turns you round), at a walk; a live combo gets its run out. */
export function stepOff(sk: Skater): void {
  if (sk.speed < 0) sk.yaw += Math.PI;
  const runOut = sk.combo.active ? RUN_OUT : 0;
  if (sk.manual) sk.manualCarry = sk.manual.balance;
  sk.manual = null;
  sk.manualFrom = null;
  sk.linkWindow = 0;
  sk.revertWindow = 0;
  const foot: FootState = {
    speed: Math.min(FOOT_RUN, Math.abs(sk.speed)),
    vy: 0,
    airborne: false,
    steps: 0,
    runOut,
    ledge: null,
  };
  sk.mode = "foot";
  sk.foot = foot;
  sk.speed = 0;
  sk.crouch = 0;
  sk.charge = 0;
  sk.nollie = false;
  sk.normal = { x: 0, y: 1, z: 0 };
}

/** How much of a run out is left, 1 (just stepped off) down to 0, or null outside one: the HUD's countdown. */
export function runOutLeft(sk: Skater): number | null {
  const left = sk.foot?.runOut ?? 0;
  return left > 0 && sk.combo.active ? left / RUN_OUT : null;
}

/** How far one step takes you at this pace (m): `FOOT_STEP_WALK` at a walk, growing to `FOOT_STEP_RUN` at a full run (#122). */
export function footStepLength(speed: number): number {
  const run = Math.min(1, Math.max(0, (Math.abs(speed) - FOOT_BACK) / (FOOT_RUN - FOOT_BACK)));
  return FOOT_STEP_WALK + (FOOT_STEP_RUN - FOOT_STEP_WALK) * run;
}

/** The pack's item, if it's a ride, is what you get on (#141); with the gun out it's the last ride you were on. */
function mount(sk: Skater): void {
  if (isRide(sk.pack.item)) sk.ride = sk.pack.item;
}

/** Drops the board (or the ride that's out) and hops on, rolling off the way you face at your running pace (at least `FOOT_BOARD_ON`). */
export function boardOn(sk: Skater, foot: FootState): void {
  mount(sk);
  sk.mode = "ground";
  sk.foot = null;
  sk.speed = Math.max(FOOT_BOARD_ON, foot.speed);
  sk.y = surfaceAt(sk.level, sk.x, sk.z).h;
  sk.setNormal(surfaceAt(sk.level, sk.x, sk.z));
  // In a run out, the combo waits like after a landing: manual to keep it.
  if (foot.runOut > 0 && sk.combo.active) sk.linkWindow = LAND_LINK_WINDOW;
}

/**
 * Caveman: throws the board under your feet mid-jump and you're riding it in
 * the air, carried on at your running pace (at least `FOOT_BOARD_ON`), with
 * the trick in a fresh combo. Landing it banks the combo like any air.
 */
export function caveman(sk: Skater, foot: FootState, c: Chord): void {
  undoChord(sk, c);
  mount(sk);
  sk.nollie = false;
  sk.foot = null;
  sk.speed = 0;
  const pace = foot.speed < 0 ? foot.speed : Math.max(FOOT_BOARD_ON, foot.speed);
  const f = sk.forward;
  enterAir(sk, f.x * pace, foot.vy, f.z * pace);
  noteAirTrick(sk, sk.addTrick("Caveman", CAVEMAN_POINTS));
}

export function stepFoot(sk: Skater, foot: FootState, input: InputFrame, chord: Chord | null, dt: number): void {
  // Hanging off a ledge there's no board under you to drop: the triggers do nothing.
  if (chord && !foot.ledge) {
    if (foot.airborne) caveman(sk, foot, chord);
    else boardOn(sk, foot);
    return;
  }
  if (foot.runOut > 0) {
    foot.runOut -= dt;
    if (foot.runOut <= 0) {
      foot.runOut = 0;
      sk.bankCombo();
    }
  }
  if (foot.ledge) {
    stepLedge(sk, foot, foot.ledge, input, dt);
    return;
  }
  sk.yaw -= input.moveX * FOOT_TURN * dt;
  // Arcade: the stick keeps working in a jump, so you can hop up onto something from a standstill.
  const want = input.moveY > 0 ? input.moveY * FOOT_RUN : input.moveY < 0 ? input.moveY * FOOT_BACK : 0;
  const d = want - foot.speed;
  foot.speed += Math.max(-FOOT_ACCEL * dt, Math.min(FOOT_ACCEL * dt, d));
  if (input.pressed.ollie && !foot.airborne) {
    foot.vy = FOOT_JUMP;
    foot.airborne = true;
  }

  // Where this step would take us, and whether we can go there.
  const f = sk.forward;
  const here = surfaceAt(sk.level, sk.x, sk.z);
  // Spraying the power washer (#151) standing on a slope, its kick shoves you back a little.
  const shove = !foot.airborne && sk.pack.item === "washer" && input.held.flip && Math.hypot(here.gx, here.gz) > WASH_SHOVE_SLOPE ? WASH_SHOVE : 0;
  const nx = sk.x + f.x * (foot.speed - shove) * dt;
  const nz = sk.z + f.z * (foot.speed - shove) * dt;
  const there = surfaceAt(sk.level, nx, nz);
  const rise = there.h - Math.max(sk.y, here.h);
  const steepUp = rise > 0 && Math.hypot(there.gx, there.gz) > FOOT_MAX_SLOPE;
  const blocked =
    sk.outOfBounds(nx, nz) ||
    (sk.level.edges !== undefined && groundKind(sk.level, nx, nz) === "water") ||
    there.h > sk.y + FOOT_STEP + STEP_EPS ||
    (!foot.airborne && steepUp) ||
    railHit(sk.level, sk.x, sk.z, nx, nz, sk.y) !== null;
  if (blocked) {
    foot.speed = 0;
    const ledge = foot.airborne ? ledgeAhead(sk, foot, here.h, there.h) : null;
    if (ledge) {
      foot.ledge = ledge;
      foot.vy = 0;
      foot.airborne = false;
      return;
    }
  } else {
    sk.x = nx;
    sk.z = nz;
    foot.steps += (Math.abs(foot.speed) * dt) / footStepLength(foot.speed);
  }

  const floor = surfaceAt(sk.level, sk.x, sk.z).h;
  if (foot.airborne) {
    foot.vy -= G * dt;
    sk.y += foot.vy * dt;
    if (sk.y <= floor) {
      sk.y = floor;
      foot.vy = 0;
      foot.airborne = false;
    }
  } else if (floor < sk.y - FOOT_DROP) {
    // Walked off something: fall.
    foot.airborne = true;
    foot.vy = 0;
  } else sk.y = floor;
}

/** How quickly you settle into the hang after catching a ledge (1/s). */
const HANG_SETTLE = 12;

/**
 * In a jump, blocked by something too high to step onto: a ledge you can
 * catch if you're at the top of the jump or on the way down, its top is in
 * reach, it's a real edge (not a slope) and there's flat ground to stand on
 * just over it.
 */
function ledgeAhead(sk: Skater, foot: FootState, hereH: number, top: number): LedgeState | null {
  if (foot.vy > 1 || top > sk.y + FOOT_REACH || top - hereH <= FOOT_STEP + STEP_EPS) return null;
  const f = sk.forward;
  const x = sk.x + f.x * FOOT_LEDGE_IN;
  const z = sk.z + f.z * FOOT_LEDGE_IN;
  const on = surfaceAt(sk.level, x, z);
  if (
    on.quarter ||
    Math.abs(on.h - top) > 0.05 ||
    Math.hypot(on.gx, on.gz) > FOOT_MAX_SLOPE ||
    sk.outOfBounds(x, z) ||
    (sk.level.edges !== undefined && groundKind(sk.level, x, z) === "water") ||
    railHit(sk.level, sk.x, sk.z, x, z, top) !== null
  )
    return null;
  return { top, x, z, x0: sk.x, y0: Math.max(hereH, top - FOOT_HANG), z0: sk.z, climb: -1 };
}

/**
 * Hanging: you settle down to arm's length below the top; up or X climbs,
 * down lets go. Climbing: up to the top first, then over onto it.
 */
function stepLedge(sk: Skater, foot: FootState, ledge: LedgeState, input: InputFrame, dt: number): void {
  if (ledge.climb < 0) {
    if (input.moveY < -0.5) {
      foot.ledge = null;
      foot.airborne = true;
      foot.vy = 0;
    } else if (input.moveY > 0.5 || input.pressed.ollie) {
      ledge.climb = 0;
      ledge.y0 = sk.y;
    } else {
      sk.y = ledge.y0 + (sk.y - ledge.y0) * Math.exp(-HANG_SETTLE * dt);
      if (Math.abs(sk.y - ledge.y0) < 1e-3) sk.y = ledge.y0;
    }
    return;
  }
  ledge.climb = Math.min(1, ledge.climb + dt / FOOT_CLIMB);
  const up = Math.min(1, ledge.climb / 0.6);
  const over = Math.max(0, (ledge.climb - 0.6) / 0.4);
  sk.y = ledge.y0 + (ledge.top - ledge.y0) * (1 - (1 - up) * (1 - up));
  sk.x = ledge.x0 + (ledge.x - ledge.x0) * over;
  sk.z = ledge.z0 + (ledge.z - ledge.z0) * over;
  if (ledge.climb >= 1) {
    foot.ledge = null;
    sk.y = surfaceAt(sk.level, sk.x, sk.z).h;
  }
}
